/*
 * El espacio de trabajo de una cuenta: /cuentas/:id/:pestana. Es el centro de
 * gravedad de la aplicación. Cada pestaña es deep-linkeable.
 *
 * Las pestañas Proyecto y Fiscal solo aparecen cuando la cuenta tiene proyectos
 * o cobros (o ya es cliente): una pestaña vacía enseña a ignorar la barra.
 */

import { Link, useNavigate, useParams } from "react-router-dom";

import { useCuenta } from "../../api/hooks";
import { FASES, nombreEquipo, type CuentaDetalle, type CasoResumen } from "../../api/tipos";
import { useFormularios } from "../../shell/Formularios";
import { Esqueleto, MarcaRelacional, PastillaEtapa } from "../../ui/primitivas";
import { DescubrimientoCuenta } from "./DescubrimientoCuenta";
import { Evidencia } from "./Evidencia";
import { Fiscal } from "./Fiscal";
import { Interacciones } from "./Interacciones";
import { Playground } from "./Playground";
import { Propuestas } from "./Propuestas";
import { ProyectoTab } from "./ProyectoTab";
import { Resumen } from "./Resumen";

export const PESTANAS = [
  { id: "resumen", label: "Resumen" },
  { id: "interacciones", label: "Interacciones" },
  { id: "evidencia", label: "Evidencia" },
  { id: "descubrimiento", label: "Descubrimiento" },
  { id: "playground", label: "Playground" },
  { id: "propuestas", label: "Propuestas" },
  { id: "proyecto", label: "Proyecto" },
  { id: "fiscal", label: "Fiscal" },
] as const;
export type Pestana = (typeof PESTANAS)[number]["id"];

/** El expediente que se está trabajando: el más reciente que no terminó en F2. */
export function casoActivo(c: CuentaDetalle): CasoResumen | null {
  const abiertos = c.casos.filter((k) => k.estado !== "F2");
  return abiertos[abiertos.length - 1] ?? c.casos[c.casos.length - 1] ?? null;
}

export function Cuenta() {
  const { id = "", pestana = "resumen" } = useParams();
  const nav = useNavigate();
  const { abrir } = useFormularios();
  const { data: c, isLoading, error } = useCuenta(id);
  const ir = (p: Pestana) => nav(`/cuentas/${id}/${p}`);

  if (isLoading) return <div className="pagina" style={{ maxWidth: 1100 }}><Esqueleto lineas={5} alto={18} /></div>;
  if (error || !c) {
    return (
      <div className="pagina" style={{ maxWidth: 1100 }}>
        <Link to="/cuentas" style={{ color: "var(--muted)", fontSize: 12.5 }}>← Cuentas</Link>
        <p style={{ marginTop: 12, color: "var(--faint)" }}>Cuenta no encontrada.</p>
      </div>
    );
  }

  const caso = casoActivo(c);
  const f = FASES[c.fase];
  const conComercial = c.etapa === "cliente" || c.proyectos.length > 0 || c.pagos.length > 0;
  const pestanas = PESTANAS.filter((p) => conComercial || (p.id !== "proyecto" && p.id !== "fiscal"));
  const tab = (pestanas.some((p) => p.id === pestana) ? pestana : "resumen") as Pestana;
  // La acción principal depende de dónde está la cuenta. Una sola, la siguiente.
  const porArrancar = c.proyectos.find((p) => p.estado === "por_arrancar");
  const siguiente =
    porArrancar ? { l: "Arrancar proyecto", go: () => abrir({ tipo: "arrancar", cuentaId: c.id, proyectoId: porArrancar.id, nombre: porArrancar.nombre, empresa: c.empresa }) }
    : c.etapa === "cliente" ? { l: "Nuevo cobro", go: () => abrir({ tipo: "pago", cuenta: c }) }
    : c.propuestas.some((p) => p.estado === "enviada") ? { l: "Ver propuestas", go: () => ir("propuestas") }
    : caso && caso.estado !== "D6" && caso.estado !== "F2" ? { l: "Ir al descubrimiento", go: () => ir("descubrimiento") }
    : caso?.estado === "D6" ? { l: "Redactar propuesta", go: () => abrir({ tipo: "propuesta", cuenta: c, casoId: caso.id }) }
    : { l: "Iniciar descubrimiento", go: () => abrir({ tipo: "descubrimiento", cuentaId: c.id, empresa: c.empresa }) };

  return (
    <div className="pagina" style={{ maxWidth: 1100, paddingTop: 28 }}>
      <Link to="/cuentas" style={{ color: "var(--muted)", fontSize: 12.5, display: "inline-block", marginBottom: 10 }}>← Cuentas</Link>
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, marginBottom: 6, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="titulo" style={{ fontSize: 26, display: "flex", alignItems: "center", gap: 10 }}>{c.empresa}<MarcaRelacional show={c.relacional} /></h1>
          <p style={{ color: "var(--muted)", fontSize: 13.5, marginTop: 2 }}>{[c.giro, c.ubicacion].filter(Boolean).join(" · ")}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 4, flexWrap: "wrap" }}>
          <PastillaEtapa etapa={c.etapa} />
          {caso && <span className="mono" style={{ color: "var(--muted)" }} title={f.nombre}>{caso.estado}</span>}
          <span title="Responsable" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted)", marginRight: 6 }}>
            <span style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--navy)", color: "#fff", fontSize: 10, fontWeight: 600, display: "grid", placeItems: "center" }}>{nombreEquipo(c.responsable_id)[0]}</span>{nombreEquipo(c.responsable_id)}
          </span>
          <button className="btn btn-secundario" onClick={() => abrir({ tipo: "interaccion", cuentaId: c.id })}>+ Interacción</button>
          <button className="btn btn-primario" onClick={siguiente.go}>{siguiente.l} →</button>
        </div>
      </header>
      <nav style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--border)", margin: "18px 0 22px", overflowX: "auto" }} aria-label="Pestañas de la cuenta">
        {pestanas.map((p) => (
          <button key={p.id} onClick={() => ir(p.id)} aria-current={tab === p.id ? "page" : undefined}
            style={{ padding: "8px 12px", fontSize: 13.5, fontWeight: 500, color: tab === p.id ? "var(--text)" : "var(--muted)", borderBottom: tab === p.id ? "2px solid var(--sky)" : "2px solid transparent", marginBottom: -1, whiteSpace: "nowrap" }}>{p.label}</button>
        ))}
      </nav>
      {tab === "resumen" && <Resumen c={c} caso={caso} ir={ir} />}
      {tab === "interacciones" && <Interacciones c={c} />}
      {tab === "evidencia" && <Evidencia c={c} caso={caso} />}
      {tab === "descubrimiento" && <DescubrimientoCuenta c={c} caso={caso} />}
      {tab === "playground" && <Playground c={c} caso={caso} />}
      {tab === "propuestas" && <Propuestas c={c} caso={caso} ir={ir} />}
      {tab === "proyecto" && <ProyectoTab c={c} />}
      {tab === "fiscal" && <Fiscal c={c} />}
    </div>
  );
}
